import React, { useEffect } from 'react';
import '../../styles/onboardingFlow/onboarding/onboarding.scss';
import OnboardingSidebar from './OnboardingSidebar';
import OnboardingMain from './OnboardingMain';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import {
  migrateOnboardingSteps,
  setAiOnboardingNextStep,
} from '../../store/reducers/userSlice';

// Streamlined onboarding: a single account-type screen. There is no longer a
// multi-step wizard, so we drop the old 5-step migration and the
// "steps 3-5 require a shop" gating. We still clamp the redux step to 1 so a
// stale/old backend step number (e.g. a user mid-old-onboarding) can never
// render a non-existent step or trap them on a blank screen.
const Onboarding = () => {
  const dispatch = useDispatch();
  const reduxCurrentStep = useSelector(
    (state: RootState) => state.user.ai_onboarding.currentStep
  );
  const completedSteps = useSelector(
    (state: RootState) => state.user.ai_onboarding.steps
  );

  // Only one step exists now; always render it.
  const currentStep = 1;

  // Normalize any persisted multi-step array (from a user who started the old
  // 5-step onboarding before this change shipped) down to the single-step
  // model. Replaces the old `completedSteps.length !== 5` migration trigger.
  useEffect(() => {
    const isSingleStepShape =
      completedSteps.length === 1 && completedSteps[0]?.number === 1;
    if (!isSingleStepShape) {
      dispatch(migrateOnboardingSteps());
    }
  }, [completedSteps, dispatch]);

  // Normalize any persisted/server step number back to the single step so the
  // sidebar highlight and downstream logic stay consistent.
  useEffect(() => {
    if (reduxCurrentStep !== 1) {
      dispatch(setAiOnboardingNextStep(1));
    }
  }, [reduxCurrentStep, dispatch]);

  const isStepCompleted = (stepNumber: number) => {
    return completedSteps[stepNumber - 1]?.isCompleted || false;
  };

  // The single step is always accessible.
  const isStepAccessible = (_stepNumber: number) => true;

  const handleStepChange = (stepNumber: number) => {
    dispatch(setAiOnboardingNextStep(stepNumber));
  };

  return (
    <div className='onboarding'>
      <div className='onboarding__sidebar'>
        <OnboardingSidebar
          currentStep={currentStep}
          onStepChange={handleStepChange}
          isStepCompleted={isStepCompleted}
          isStepAccessible={isStepAccessible}
        />
      </div>
      <div className='onboarding__main'>
        <OnboardingMain
          currentStep={currentStep}
          onStepChange={handleStepChange}
        />
      </div>
    </div>
  );
};

export default Onboarding;
